import ENV from "config/Env";
import { backendQueryKey, localQueryKey, permanentBackendQueryKey } from "hooks/useTypedQuery";

import type { Appointment, Brand, CancellationReason, Car, CarCheck, Checklist, QuestionResult, Snooze, User } from "models";
import type { PonOvisEvent } from "modules/AppointmentDetails/components";
import type { LiveUser } from "modules/AppointmentDetails/hooks";
import type { PaginatedItems } from "utils/paginatedItems";

const component = "AppointmentDetails";

// TODO: ask backend to rename question_results to items and nb_count to nb_items to be consistent and remove this type to use PaginatedItem<QuestionResult> instead
export type PaginatedQuestionResults = {
  question_results: QuestionResult[] | null;
  nb_pages: number;
  nb_count: number;
};

type OCWVehicleStatusResponse = {
  mileage: string | null;
  enrolled: boolean;
  workshop_mode: boolean;
  is_connect_capable: boolean;
};

type TyreLimit = {
  TyreType: string;
  AdviceTreadDepth: number;
  NecessaryTreadDepth: number;
};

export type AdvisedCriticalItemsHistoryParams = {
  page: number;
  hidden_items: boolean;
  car_id?: number;
  appointment_id?: number;
};

export const AppointmentDetailsKeys = {
  advisedCriticalItemsHistory: (params: AdvisedCriticalItemsHistoryParams) =>
    backendQueryKey<PaginatedQuestionResults>({
      component,
      baseUrl: ENV.appointmentBaseURL,
      endpoint: "/leads/advised_critical_history",
      params
    }),
  attachments: backendQueryKey<string[]>({
    component,
    baseUrl: ENV.appointmentBaseURL,
    endpoint: "/notes/attachment/url/list"
  }),
  cancellationReasons: permanentBackendQueryKey<CancellationReason[]>({ component, endpoint: "/status/cancel_reasons", baseUrl: ENV.appointmentBaseURL }),
  carBrands: permanentBackendQueryKey<Brand[]>({
    component,
    baseUrl: ENV.carBaseURL,
    endpoint: "/brands/list"
  }),
  checklists: (locationId: number) =>
    permanentBackendQueryKey<Checklist[]>({
      component,
      baseUrl: ENV.dealerBaseURL,
      endpoint: "/checklists/list",
      params: { locationId }
    }),
  checks: backendQueryKey<CarCheck[]>({
    component,
    baseUrl: ENV.appointmentBaseURL,
    endpoint: "/checks/list",
    params: { embed: "pin,snooze" }
  }),
  customerOwners: (locationId: number) =>
    permanentBackendQueryKey<User[]>({
      component,
      baseUrl: ENV.systemBaseURL,
      endpoint: "/users/list_by_role_and_location",
      params: { locationId }
    }),
  dbbPortalUrl: backendQueryKey<string>({
    component,
    baseUrl: ENV.appointmentBaseURL,
    endpoint: "/dbb/portal"
  }),
  dbbRefreshedCar: backendQueryKey<Car>({
    component,
    baseUrl: ENV.appointmentBaseURL,
    endpoint: "/dbb/refresh"
  }),
  logs: backendQueryKey<string>({
    component,
    baseUrl: ENV.appointmentBaseURL,
    endpoint: "/logs/view"
  }),
  ponOCWEnrollmentLink: backendQueryKey<string | null>({
    component,
    baseUrl: ENV.carBaseURL,
    endpoint: "/pon/ocw/enrollment_portal"
  }),
  ponOCWServiceLink: backendQueryKey<string | null>({
    component,
    baseUrl: ENV.carBaseURL,
    endpoint: "/pon/ocw/service_portal"
  }),
  ponOCWVehicleStatus: backendQueryKey<OCWVehicleStatusResponse | null>({
    component,
    baseUrl: ENV.carBaseURL,
    endpoint: "/pon/ocw/vehicle_status"
  }),
  ponOvisEvents: backendQueryKey<PonOvisEvent[]>({
    component,
    baseUrl: ENV.carBaseURL,
    endpoint: "/pon/ovis/events"
  }),
  snoozeAppointment: backendQueryKey<Appointment>({
    component,
    baseUrl: ENV.appointmentBaseURL,
    endpoint: "/appointments/view"
  }),
  snoozedItemsHistory: (appointment_id: number, page: number) =>
    backendQueryKey<PaginatedItems<Snooze>>({
      component,
      baseUrl: ENV.appointmentBaseURL,
      endpoint: "/snoozes/appointment_history",
      params: { appointment_id, page }
    }),
  tyreLimits: backendQueryKey<TyreLimit[]>({
    component,
    baseUrl: ENV.appointmentBaseURL,
    endpoint: "/dbb/tyre_limits/list"
  }),
  view: backendQueryKey<Appointment>({
    component,
    baseUrl: ENV.appointmentBaseURL,
    endpoint: "/appointments/view",
    params: { embed: "appointment_status,customer_owner,customer_driver,car,customer_contract,customcomv2,pin,snooze,deskcom,keycom,kioskcom" }
  }),
  connectedQuestionResultIdToFilter: localQueryKey<number | null>({ component, data: "connectionQuestionResultId" }),
  connectedInterventionIdToFilter: localQueryKey<number | null>({ component, data: "connectedInterventionIdToFilter" }),
  otherUsersOnTheSameAppointmentDetailsPage: localQueryKey<LiveUser[]>({ component, data: "otherUsersOnTheSameAppointmentDetailsPage" }),
  showPriceInVat: localQueryKey<boolean>({ component, data: "showPriceInVat" })
};
